import { type ChangeEvent, type KeyboardEvent, useRef, useState } from 'react';
import type { AuthMethod, ConnectorPlatform, SyncCadence } from '@/entities/media-connector';
import {
  PLATFORM_AUTH_METHODS,
  PLATFORM_DEFAULT_CADENCE,
  PLATFORMS_REQUIRING_AN_ADVERTISER,
} from '@/entities/media-connector';
import type { AddAccountSubmitPayload } from '../AddAccountDialog';

interface UseAddAccountFormArgs {
  platform: ConnectorPlatform;
  onSubmit: (payload: AddAccountSubmitPayload) => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * The validation and submit shape of `AddAccountDialog`, relocated here so the
 * sheet stays presentational. The Google Ads manager-account id, the DV360-style
 * advertiser id and the CM360 account id are the only platform-conditional
 * fields; see `PLATFORM_AUTH_METHODS` / `PLATFORMS_REQUIRING_AN_ADVERTISER` for
 * why. Only the sheet asks for the CM360 account id, and only under OAuth: a
 * Google login can reach several CM360 accounts, and the service-account
 * connect has no field to carry it.
 */
export function useAddAccountForm({ platform, onSubmit, onOpenChange }: UseAddAccountFormArgs) {
  const supportedMethods = PLATFORM_AUTH_METHODS[platform];
  const hasMultipleMethods = supportedMethods.length > 1;
  const defaultCadence = PLATFORM_DEFAULT_CADENCE[platform];
  const usesGoogleAdsApi = platform === 'google_ads' || platform === 'youtube_ads';
  const needsAdvertiser = PLATFORMS_REQUIRING_AN_ADVERTISER.includes(platform);

  const [title, setTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [authMethod, setAuthMethod] = useState<AuthMethod>(supportedMethods[0]);
  const [syncCadence, setSyncCadence] = useState<SyncCadence>(defaultCadence);
  const [token, setToken] = useState('');
  const [keyFileContent, setKeyFileContent] = useState('');
  const [loginCustomerId, setLoginCustomerId] = useState('');
  const [advertiserId, setAdvertiserId] = useState('');
  const [cm360AccountId, setCm360AccountId] = useState('');
  const [keyFileName, setKeyFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const asksForCm360Account = platform === 'cm360' && authMethod === 'oauth';

  const reset = () => {
    setTitle('');
    setTags([]);
    setTagInput('');
    setAuthMethod(supportedMethods[0]);
    setSyncCadence(defaultCadence);
    setToken('');
    setKeyFileContent('');
    setLoginCustomerId('');
    setAdvertiserId('');
    setCm360AccountId('');
    setKeyFileName('');
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  const addTag = (value: string) => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === 'Backspace' && !tagInput && tags.length > 0) {
      setTags(tags.slice(0, -1));
    }
  };

  const handleTagBlur = () => {
    if (tagInput.trim()) addTag(tagInput);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setKeyFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      setKeyFileContent(reader.result as string);
    };
    reader.readAsText(file);
  };

  const isValid = Boolean(
    title.trim() &&
    (!needsAdvertiser || advertiserId.trim() !== '') &&
    (authMethod === 'oauth' ||
      (authMethod === 'system_user_token' && token.trim()) ||
      (authMethod === 'service_account' && keyFileContent)),
  );

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      title: title.trim(),
      tags,
      authMethod,
      syncCadence,
      ...(authMethod === 'system_user_token' && { token: token.trim() }),
      ...(authMethod === 'service_account' && { keyFileContent }),
      ...(needsAdvertiser && advertiserId.trim() !== ''
        ? { advertiserId: advertiserId.trim() }
        : {}),
      ...(usesGoogleAdsApi && loginCustomerId.trim() !== ''
        ? { loginCustomerId: loginCustomerId.trim() }
        : {}),
      ...(asksForCm360Account && cm360AccountId.trim() !== ''
        ? { accountId: cm360AccountId.trim() }
        : {}),
    });
    if (authMethod === 'oauth') {
      handleClose(false);
    }
  };

  return {
    supportedMethods,
    hasMultipleMethods,
    needsAdvertiser,
    usesGoogleAdsApi,
    title,
    setTitle,
    tags,
    tagInput,
    setTagInput,
    handleTagKeyDown,
    handleTagBlur,
    removeTag,
    authMethod,
    setAuthMethod,
    syncCadence,
    setSyncCadence,
    token,
    setToken,
    keyFileName,
    handleFileChange,
    fileInputRef,
    loginCustomerId,
    setLoginCustomerId,
    advertiserId,
    setAdvertiserId,
    asksForCm360Account,
    cm360AccountId,
    setCm360AccountId,
    isValid,
    handleSubmit,
    handleClose,
  };
}
